import { FC, useCallback } from 'react'

import { fetchQuery } from '@sequencehq/api'
import {
  Dashboardv99990101Api,
  dashboardv99990101Client
} from '@sequencehq/api/dashboard/v99990101'
import { AsyncInputSelectControlField } from '@sequencehq/core-components'
import { apiQueryClient } from 'features/api/apiQueryClient'
import {
  NETSUITE_ITEM_ACCOUNT_DEFAULT_LABEL,
  NETSUITE_ITEM_STALE_TIME,
  netsuiteItemFallbackLabel
} from './netsuiteItem.constants'

type NetsuiteItemOption = {
  label: string
  value: string
  disabled?: boolean
}

const ACCOUNT_DEFAULT_OPTION: NetsuiteItemOption = {
  label: 'Use account default',
  value: 'accountDefault'
}

const READ_ONLY_ACCOUNT_DEFAULT_OPTION: NetsuiteItemOption = {
  ...ACCOUNT_DEFAULT_OPTION,
  label: NETSUITE_ITEM_ACCOUNT_DEFAULT_LABEL
}

type NetsuiteItem = Dashboardv99990101Api.SearchNetsuiteItems.NetsuiteItem

const NO_MATCHES_VALUE = 'noMatches'
const SEARCH_FAILED_VALUE = 'searchFailed'
const STATUS_ROW_VALUES = [NO_MATCHES_VALUE, SEARCH_FAILED_VALUE]

/**
 * Picks a NetSuite item by searching as the user types, so we never pull the
 * whole item list down. "Use account default" means no item, so it's always
 * the first row - even when the search fails, which is why the fetcher catches
 * its own errors rather than letting the select swap every row for its error
 * state.
 */
export const NetsuiteItemSelect: FC<{
  value: string | undefined
  /** `undefined` means the account default. `name` is missing if we lost it. */
  onChange: (item: { id: string; name?: string } | undefined) => void
  label?: string
  disabled?: boolean
  'data-testid': string
}> = ({ value, onChange, label, disabled, 'data-testid': dataTestId }) => {
  // A read-only field shows what the grant uses, not an offer to pick.
  const accountDefaultOption = disabled
    ? READ_ONLY_ACCOUNT_DEFAULT_OPTION
    : ACCOUNT_DEFAULT_OPTION

  const fetcher = useCallback(
    async (term: string): Promise<NetsuiteItemOption[]> => {
      const q = term.trim()

      if (!q) {
        return [accountDefaultOption]
      }

      try {
        const data = await fetchQuery(
          apiQueryClient,
          dashboardv99990101Client.searchNetsuiteItems,
          { q }
        )
        const items = data?.items ?? []

        if (!items.length) {
          return [
            ACCOUNT_DEFAULT_OPTION,
            {
              label: `No NetSuite items match "${q}"`,
              value: NO_MATCHES_VALUE,
              disabled: true
            }
          ]
        }

        return [
          ACCOUNT_DEFAULT_OPTION,
          ...items.map(item => ({ label: item.name, value: item.id }))
        ]
      } catch {
        return [
          ACCOUNT_DEFAULT_OPTION,
          {
            label: "Couldn't search NetSuite items",
            value: SEARCH_FAILED_VALUE,
            disabled: true
          }
        ]
      }
    },
    [accountDefaultOption]
  )

  // The select forgets an item's name once the drawer closes, so a reopened
  // drawer has only the id. A pick from this session is still in the search
  // cache, so we reuse that and skip the lookup. If the lookup fails we show
  // the id rather than hold up the drawer.
  const resolveValue = useCallback(
    async (itemId: string): Promise<NetsuiteItemOption> => {
      if (itemId === ACCOUNT_DEFAULT_OPTION.value) {
        return accountDefaultOption
      }

      const searchedItem = findSearchedItem(itemId)

      if (searchedItem) {
        return { label: searchedItem.name, value: itemId }
      }

      try {
        const item = await fetchQuery(
          apiQueryClient,
          dashboardv99990101Client.getNetsuiteItem,
          { id: itemId },
          { staleTime: NETSUITE_ITEM_STALE_TIME }
        )
        return {
          label: item?.name ?? netsuiteItemFallbackLabel(itemId),
          value: itemId
        }
      } catch {
        return { label: netsuiteItemFallbackLabel(itemId), value: itemId }
      }
    },
    [accountDefaultOption]
  )

  const handleChange = useCallback(
    (newValue: string) => {
      // Keyboard selection doesn't skip disabled rows, so ignore the status rows here.
      if (STATUS_ROW_VALUES.includes(newValue)) {
        return
      }

      // The picked row came from a search we ran, but the cache drops it after
      // a few idle minutes. Then we pass no name rather than the bare id.
      onChange(
        newValue === ACCOUNT_DEFAULT_OPTION.value
          ? undefined
          : (findSearchedItem(newValue) ?? { id: newValue })
      )
    },
    [onChange]
  )

  return (
    <AsyncInputSelectControlField
      label={label}
      data-testid={dataTestId}
      fetcher={fetcher}
      mapToOption={option => option}
      value={value ?? ACCOUNT_DEFAULT_OPTION.value}
      onChange={handleChange}
      resolveValue={resolveValue}
      disabled={disabled}
      placeholder="Search NetSuite items"
    />
  )
}

const findSearchedItem = (itemId: string): NetsuiteItem | undefined =>
  apiQueryClient
    .getQueriesData<Dashboardv99990101Api.SearchNetsuiteItems.SearchNetsuiteItemsResponse>(
      {
        queryKey: dashboardv99990101Client.searchNetsuiteItems.queryKey
      }
    )
    .flatMap(([, data]) => data?.items ?? [])
    .find(item => item.id === itemId)
